import { LocaleIdentifier } from '@repo/markets/types'
import { NavigationQueryResult } from '@repo/sanity/types'
import { Button, ButtonVariant } from '@repo/ui/components/button'
import { cn } from '@repo/ui/lib/utils'
import { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import Marquee from 'react-fast-marquee'
import { useNavigation } from '../../../providers/NavigationProvider'
import LinkResolver from '../../links/LinkResolver'

interface Props {
  locale: LocaleIdentifier
  navigation: NavigationQueryResult
  className?: string
}

/**
 * Marquee element.
 *
 * @see https://github.com/justin-chu/react-fast-marquee
 */
const Scroller: FunctionComponent<{ message: string }> = ({ message }) => {
  return (
    <Marquee speed={25}>
      <span className='relative top-px box-content px-1'>{message.trim()}</span>
    </Marquee>
  )
}

const AnnouncementButton: FunctionComponent<Props> = ({ locale, navigation, className }) => {
  const { shouldUseInverse } = useNavigation()
  const message = navigation?.announcementSettings?.message || undefined
  const buttonClassName = cn(
    'flex w-24 select-none overflow-hidden px-1.5 leading-none sm:w-32 md:w-40 lg:w-24 xl:w-28',
    !navigation?.announcementSettings?.link && 'pointer-events-none',
    className,
  )

  const buttonProps: { variant: ButtonVariant; asChild: boolean } & ComponentPropsWithoutRef<'button'> = {
    variant: shouldUseInverse(navigation) ? 'navigationInverse' : 'navigationDefault',
    className: buttonClassName,
    asChild: true,
  }

  if (!message) {
    return null
  }

  const scroller = <Scroller message={message} />

  const inner = navigation?.announcementSettings?.link ? (
    <Button {...buttonProps}>
      <LinkResolver link={navigation?.announcementSettings?.link} locale={locale} scroll={false}>
        {scroller}
      </LinkResolver>
    </Button>
  ) : (
    <Button {...buttonProps}>
      <div>{scroller}</div>
    </Button>
  )

  return <li>{inner}</li>
}

export default AnnouncementButton
